/* eslint-disable max-statements */
/* eslint-disable new-cap */
import { Backsplash } from '../panels/backsplash.class';
import { BlueprintInstanceType } from '../shared.enums';
import { Breakpoints } from '../breakpoints/breakpoints.class';
import { countertopData } from './countertop.data';
import { CountertopMaterial } from '../countertop-material/countertop-material.class';
import { KitchenDesign } from '../kitchen-design/kitchen-design.class';
import { Panels } from '../panels/panels.class';
import { Publish } from '../../decorators';
import { ReferenceMap } from '../shared.types';
import { Size } from '../../types';
import { CountertopDataIn, CountertopDataOut } from './types';
import { getCountertopMsrpCfn, getCountertopSkuCfn, getRenderingDetailsCfn } from './functions';
import { KitchenDesignBase, KitchenDesignOptions } from '../kitchen-design-base';
import { RenderDetails, Vertex3 } from '../../spatial-classes';

export class Countertop extends KitchenDesignBase<CountertopDataIn> {
	#material: CountertopMaterial;
	#breakpoints: Breakpoints;
	#backsplashes: Panels;
	#islandIds: Array<string>;

	/** Override is metadata used by the consuming class to deetermine if this countertop has been manually set.
	 * Typically the countertop is selected for the kitchen.  When a new kitchen countertop is selected, this value cascades to each island.
	 * if an island has had a different countertop selected, override would be true. Setting override to true on the island prevents kitchen
	 * level selections from overriding what was selected for the island.
	 */
	#override: boolean | null;

	constructor (options: KitchenDesignOptions<CountertopDataIn> = {}) {
		super(BlueprintInstanceType.Countertop, options);
		// console.log('Countertop->constructor', options, this.refMap);
		const { dataIn = null } = options;
		const data = countertopData(dataIn);

		this._id = data.id;
		this.#override = data.override;
		this.#breakpoints = new Breakpoints({ dataIn: data.breakpoints, parent: this });
		this.#backsplashes = new Panels({ dataIn: data.backsplashes, parent: this });
		this.#islandIds = data.islandIds;

		// console.log('Countertop->constructor', this.refMap?.kitchen.material.countertop);
		this.#material = new CountertopMaterial({ dataIn: data.material, parent: this, syncTo: this.refMap?.kitchen.material.countertop });

		this.setInitialized();

		// Subscribe after init - callbacks only fire when fully constructed
		this.refMap.kitchen?.subscribe('overhang', 'updated', () => this.updateBacksplashes());
		this.refMap.kitchen?.subscribe('island_size', 'updated', () => this.updateBacksplashes());
	}

	get backsplashes (): Panels {
		return this.#backsplashes;
	}
	get breakpoints (): Breakpoints {
		return this.#breakpoints;
	}
	get islandIds (): Array<string> {
		return this.#islandIds;
	}
	get material (): CountertopMaterial {
		return this.override ? this.#material : this.refMap.kitchen.material.countertop;
	}
	get msrp (): number {
		return getCountertopMsrpCfn(this, this.sku);
	}
	get sku (): string {
		return getCountertopSkuCfn(this);
	}

	@Publish('countertop_override', 'updated')
	set override (v: boolean) {
		if (this.#override && v === false) {
			// keep panel material insync with kitchen countertop unless overridden
			if (this.refMap && this.refMap.kitchen) {
				this.#material.setMaterial(this.refMap.kitchen.material.countertop.key);
			}
		}
		this.#override = v;
	}
	get override (): boolean {
		return this.#override;
	}

	/**
	 * Returns the vertexes of the countertop
	 */
	get renderingDetails (): RenderDetails {
		return getRenderingDetailsCfn(this);
	}

	get center (): Vertex3 {
		const island = this.refMap.kitchen?.design.islands.findById(this.islandIds[0]);
		const overhangs = island.getOverhangs();
		const center = new Vertex3();

		if (island) {
			center.x = (overhangs.right - overhangs.left) / 2;
			center.y = -island.size.height - (this.material.thickness / 2);
			center.z = (overhangs.front - overhangs.back) / 2;
		}

		return center;
	}

	get size (): Size {
		const island = (this.findParentInstanceOf(BlueprintInstanceType.KitchenDesign) as unknown as KitchenDesign)?.islands.findById(this.islandIds[0]);
		if (!island) return { width: 0, height: 0, depth: 0 };
		const islandSize = island?.islandSize;
		const overhangs = island.getOverhangs();
		if (island) {
			const size = {
				width: islandSize.width + overhangs.left + overhangs.right,
				height: islandSize.depth + overhangs.front + overhangs.back,
				depth: this.material.thickness
			};
			return size;
		}

		return { width: 0, height: 0, depth: 0 };
	}

	/**
	 * @param panelId The ID of the panel to get the countertop width for
	 * @returns The width of the countertop on that panel's side
	 */
	public getCountertopWidthForPanel (panelId: string): number {
		const island = this.refMap.kitchen?.design.islands.findById(this.islandIds[0]);

		const panel = island.panels.findById(panelId);

		const sidePoints = panel.getSidePoints({ includeCountertop: true, translate: false, rotate: false });
		if (!sidePoints || sidePoints.corners.length < 2) {
			const { islandSize } = island;
			const overhangs = island.getOverhangs();

			if (panel.isFrontPanel || panel.isBackPanel) {
				return islandSize.width + overhangs.left + overhangs.right;
			} else if (panel.isLeftPanel || panel.isRightPanel) {
				return islandSize.depth + overhangs.front + overhangs.back;
			}
			return 0;
		}

		const [leftCorner, rightCorner] = sidePoints.corners;
		const width = Math.abs(rightCorner.x - leftCorner.x);

		return width;
	}

	/**
	 * Gets the countertop center offset for a specific panel/side.
	 * The countertop center differs from the panel center due to asymmetric overhangs.
	 * @param panelId The ID of the panel to get the countertop center offset for
	 * @returns The offset from panel center to countertop center along the panel's width axis
	 */
	public getCountertopCenterOffsetForPanel (panelId: string): number {
		const island = this.refMap.kitchen?.design.islands.findById(this.islandIds[0]);
		if (!island) return 0;

		const panel = island.panels.findById(panelId);
		if (!panel) return 0;

		const sidePoints = panel.getSidePoints({ includeCountertop: true, translate: false, rotate: false });
		if (!sidePoints || sidePoints.corners.length < 2) {
			const overhangs = island.getOverhangs();

			if (panel.isFrontPanel || panel.isBackPanel) {
				return (overhangs.right - overhangs.left) / 2;
			} else if (panel.isLeftPanel || panel.isRightPanel) {
				return (overhangs.back - overhangs.front) / 2;
			}
			return 0;
		}

		const [leftCorner, rightCorner] = sidePoints.corners;
		const countertopCenterX = (leftCorner.x + rightCorner.x) / 2;

		if (!island.isCornerIsland && (panel.isLeftPanel || panel.isBackPanel)) {
			return -countertopCenterX;
		}

		return countertopCenterX;
	}

	/**
	 * Creates a backsplash panel for a specific island panel
	 * @param islandPanelId The ID of the island panel to create a backsplash for
	 * @param options Optional configuration for the backsplash (height, etc.)
	 * @returns The created Backsplash, or null if creation failed
	 * @emits backsplash.added
	 */
	@Publish('backsplash', 'added')
	public createBacksplash (islandPanelId: string, options: { height?: number } = {}): Backsplash | null {
		if (!this.#backsplashes) return null;

		// Use the Backsplash factory method to create the backsplash
		// This keeps backsplash creation logic in the Backsplash class where it belongs
		const backsplashPanel = Backsplash.createForPanel(
			islandPanelId,
			this,
			this.#backsplashes,
			options
		);

		if (backsplashPanel) {
			this.#backsplashes.children.push(backsplashPanel);
		}

		return backsplashPanel;
	}

	/**
	 * Removes a backsplash panel by its ID
	 * @param backsplashPanelId The ID of the backsplash panel to remove
	 */
	public removeBacksplash (backsplashPanelId: string): void {
		if (this.#backsplashes) {
			const index = this.#backsplashes.children.findIndex((p) => p.id === backsplashPanelId);
			if (index !== -1) {
				const backsplash = this.#backsplashes.children[index];
				// Call cleanup to trigger the 'deleted' event (which publishes backsplash.deleted)
				backsplash.cleanup();
				this.#backsplashes.children.splice(index, 1);
			}
		}
	}

	/**
	 * Gets the backsplash for a specific island panel
	 * @param islandPanelId The ID of the island panel
	 * @returns The Backsplash if it exists, or null
	 */
	public getBacksplashForPanel (islandPanelId: string): Backsplash | null {
		return this.#backsplashes?.children.find((p) => p.label === `backsplash-${islandPanelId}`) as Backsplash ?? null;
	}

	/**
	 * Updates all backsplash panels with current countertop widths and center offsets.
	 * Call this when overhangs or island size changes to keep backsplashes aligned with countertop.
	 */
	public updateBacksplashes (): void {
		if (!this.#backsplashes) return;

		// Delegate to each backsplash's updateDimensions method
		this.#backsplashes.children.forEach((panel) => {
			const backsplash = panel as Backsplash;
			backsplash.updateDimensions(this);
		});
	}

	/**
	 *
	 * @param id reference id to find
	 * @returns ReferenceMap
	 */
	public getReferenceMap (id: string): ReferenceMap {
		return super.getReferenceMap(id, ['breakpoints', 'backsplashes']);
	}

	public serialize (): CountertopDataOut {
		const record: CountertopDataOut = {
			id: this._id,
			material: this.#material.serialize(),
			breakpoints: this.#breakpoints.serialize(),
			backsplashes: this.#backsplashes.serialize(),
			islandIds: this.#islandIds,
			sku: this.sku
		};

		if (this.#override === true) record.override = true;

		return record;
	}
}
